import { Link, useParams } from 'react-router-dom';
import type { ProjectStatus } from '@dsr/shared-types';
import {
  useExpenses,
  usePayments,
  useProject,
  useProjectFinancials,
  useUpdateProject,
} from '../api/queries/financials';
import { PROJECT_STATUSES, projectTypeLabel } from './ProjectsPage';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';
import {
  Card,
  KpiCard,
  Select,
  StatusBadge,
  Table,
  Tabs,
  type SelectOption,
  type TabItem,
} from '../components/ui';

export function ProjectDetailPage(): JSX.Element {
  const params = useParams();
  const id = Number(params['id']);
  const projectId = Number.isInteger(id) && id > 0 ? id : null;

  const { data, isLoading, isError } = useProject(projectId);
  const { data: fin } = useProjectFinancials(projectId);
  const update = useUpdateProject();

  const canWrite = can('project.write');
  const project = data?.project;

  const onStatusChange = async (status: ProjectStatus) => {
    if (!project) return;
    try {
      await update.mutateAsync({ id: project.id, status });
      toast.success(`Project marked ${status.replace('_', ' ')}.`);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Status change failed.');
    }
  };

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !project) {
    return <p className="error-text" role="alert">Project not found.</p>;
  }

  const summary = fin?.summary;

  const statusOptions: SelectOption[] = PROJECT_STATUSES.map((s) => ({
    value: s.value,
    label: s.label,
  }));

  const tabItems: TabItem[] = [
    { value: 'expenses', label: 'Expenses', content: <ProjectExpenses projectId={project.id} /> },
    { value: 'income', label: 'Income', content: <ProjectIncome projectId={project.id} /> },
  ];

  return (
    <div className="page project-detail-page">
      <header className="page-header">
        <div>
          <p className="page-sub">
            <Link to="/projects" className="table-link">Projects</Link> / {project.project_no}
          </p>
          <h1>{project.title}</h1>
          <p className="page-sub">
            {project.organization_name} · {projectTypeLabel(project.project_type)} ·{' '}
            {project.billing_type === 'flat_fee'
              ? `Flat fee ${fmtMoney(project.flat_fee_amount)}`
              : project.billing_type}
            {project.target_end_at ? ` · target ${fmtDate(project.target_end_at)}` : ''}
          </p>
        </div>
        {canWrite ? (
          <Select
            value={project.status}
            onValueChange={(v) => void onStatusChange(v as ProjectStatus)}
            options={statusOptions}
            disabled={update.isPending}
            aria-label="Project status"
          />
        ) : (
          <StatusBadge status={project.status}>{project.status.replace('_', ' ')}</StatusBadge>
        )}
      </header>

      {summary && (
        <div className="kpi-grid">
          <KpiCard label="Income (cleared)" value={fmtMoney(summary.paid_total)} />
          <KpiCard label="Expenses" value={fmtMoney(summary.expense_total)} />
          <KpiCard label="Contractor payouts" value={fmtMoney(summary.contractor_paid_total)} />
          <KpiCard label="Net" value={fmtMoney(summary.net_total)} />
        </div>
      )}

      <Tabs items={tabItems} defaultValue="expenses" />

      {project.notes && (
        <Card style={{ marginTop: 'var(--space-4)' }}>
          <Card.Header>
            <Card.Title>Notes</Card.Title>
          </Card.Header>
          <Card.Body>
            <p style={{ whiteSpace: 'pre-wrap' }}>{project.notes}</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function ProjectExpenses({ projectId }: { projectId: number }): JSX.Element {
  const { data, isLoading } = useExpenses({ project_id: projectId });
  const expenses = data?.expenses ?? [];
  if (isLoading) return <p className="muted">Loading…</p>;
  if (expenses.length === 0) {
    return (
      <p className="muted">
        No expenses on this project yet. Log them from the{' '}
        <Link to="/expenses" className="table-link">Expenses</Link> page.
      </p>
    );
  }
  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Date</Table.HeaderCell>
          <Table.HeaderCell>Type</Table.HeaderCell>
          <Table.HeaderCell>Vendor</Table.HeaderCell>
          <Table.HeaderCell>Amount</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {expenses.map((e) => (
          <Table.Row key={e.id}>
            <Table.Cell>{fmtDate(e.expense_date)}</Table.Cell>
            <Table.Cell>{e.expense_type}</Table.Cell>
            <Table.Cell tone="muted">{e.vendor_name ?? '—'}</Table.Cell>
            <Table.Cell>{fmtMoney(e.amount)}</Table.Cell>
            <Table.Cell>
              {e.reimbursed_at ? 'Reimbursed' : e.approved_at ? 'Approved' : 'Entered'}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

function ProjectIncome({ projectId }: { projectId: number }): JSX.Element {
  const { data, isLoading } = usePayments({ project_id: projectId });
  const payments = data?.payments ?? [];
  if (isLoading) return <p className="muted">Loading…</p>;
  if (payments.length === 0) {
    return (
      <p className="muted">
        No income recorded for this project yet. Record it from the{' '}
        <Link to="/income" className="table-link">Income</Link> page or by
        marking an invoice paid.
      </p>
    );
  }
  return (
    <Table>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>Date</Table.HeaderCell>
          <Table.HeaderCell>Method</Table.HeaderCell>
          <Table.HeaderCell>Invoice</Table.HeaderCell>
          <Table.HeaderCell>Reference</Table.HeaderCell>
          <Table.HeaderCell>Amount</Table.HeaderCell>
          <Table.HeaderCell>Status</Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {payments.map((p) => (
          <Table.Row key={p.id}>
            <Table.Cell>{fmtDate(p.payment_date)}</Table.Cell>
            <Table.Cell>{p.method}</Table.Cell>
            <Table.Cell>{p.invoice_no ?? '—'}</Table.Cell>
            <Table.Cell tone="muted">{p.reference_no ?? '—'}</Table.Cell>
            <Table.Cell>{fmtMoney(p.amount)}</Table.Cell>
            <Table.Cell><StatusBadge status={p.status}>{p.status}</StatusBadge></Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
